import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import projects from '../data/projects';

export default function Projects() {
  const [selectedYear, setSelectedYear] = useState('Tümü');
  
  // Yılları sıralama mantığı (Orijinal kodun aynısı)
  const years = ['Tümü', ...new Set(projects.map(p => p.year.toString()))].sort((a, b) => {
    if (a === 'Tümü') return -1;
    if (b === 'Tümü') return 1;
    return parseInt(b) - parseInt(a);
  });

  const filteredProjects = selectedYear === 'Tümü' 
    ? projects 
    : projects.filter(p => p.year.toString() === selectedYear);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Header Animation */}
        <div className="text-center mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-6xl font-serif font-bold text-navy mb-4 tracking-tight"
          >
            Projeler
          </motion.h1>
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: "6rem" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-gradient-to-r from-transparent via-navy to-transparent mx-auto mb-4"
          ></motion.div>
        </div>

        {/* Year Filter Buttons */}
        <div className="mb-10 flex flex-wrap gap-3 justify-center">
          {years.map((year) => (
            <motion.button
              key={year}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedYear(year)}
              className={`px-6 py-2.5 rounded-lg transition-colors duration-300 font-medium ${
                selectedYear === year
                  ? 'bg-navy text-white shadow-lg'
                  : 'bg-white text-navy border-2 border-gray-300 hover:border-navy hover:bg-navy hover:text-white'
              }`}
            >
              {year}
            </motion.button>
          ))}
        </div>

        {/* Projects List with Animation */}
        <motion.div layout className="space-y-8">
          <AnimatePresence mode='popLayout'>
            {filteredProjects.map((project) => (
              <motion.div
                layout // Filtreleme sırasında kutuların yer değiştirmesini animasyonlu yapar
                key={project.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                whileHover={{ y: -5 }} // Hover durumunda yukarı kalkma
                className="bg-white rounded-xl border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow duration-300"
              >
                {/* Card Header */}
                <div className="flex flex-wrap justify-between items-start mb-4 gap-4">
                  <h3 className="text-2xl font-serif font-bold text-navy flex-1 min-w-[200px]">
                    {project.title}
                  </h3>
                  
                  <div className="flex flex-col items-end gap-2 ml-auto">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                      📅 {project.year}
                    </span>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                      project.status === 'Tamamlandı' 
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' 
                        : 'bg-amber-50 border border-amber-200 text-amber-700'
                    }`}>
                      {project.status}
                    </span>
                  </div>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.role && (
                    <span className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold rounded-lg">
                      {project.role}
                    </span>
                  )}
                  {project.type && (
                    <span className="px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 text-xs font-semibold rounded-lg">
                      {project.type}
                    </span>
                  )}
                  {project.period && (
                    <span className="px-3 py-1.5 bg-gray-100 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg">
                      {project.period}
                    </span>
                  )}
                </div>
                
                {/* Description */}
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">{project.description}</p>

                {/* Link Button */}
                {project.link && (
                  <motion.a
                    whileHover={{ scale: 1.02, backgroundColor: "#1a237e" }}
                    whileTap={{ scale: 0.98 }}
                    href={project.link}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg transition-colors font-medium shadow-sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Proje Detayları
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </motion.a>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        
        {/* Empty State (Eğer filtre sonucunda proje yoksa) */}
        {filteredProjects.length === 0 && (
            <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                className="text-center py-12 text-gray-500 text-lg"
            >
                Seçilen yılda proje bulunmamaktadır.
            </motion.div>
        )}
      </div>
    </div>
  );
}